import { useEffect, useRef, useState } from "react";
import { Icon } from "../../../shell/Icon";

/**
 * Generisches Zahlen-Eingabe-Modal als Ersatz für `window.prompt()` — das
 * native Prompt crasht im eingebetteten Browser (VS-Code-Simple-Browser:
 * „prompt() is not supported"). Ein Input, positive Zahl, Komma erlaubt,
 * Enter bestätigt / Escape bricht ab. Style folgt `.modal`-Pattern
 * (siehe PreisEditDialog).
 */
export function PromptModal({
  title,
  label,
  initialValue = "",
  unit,
  confirmLabel = "Übernehmen",
  hint,
  onConfirm,
  onClose,
}: {
  title: string;
  label: string;
  initialValue?: string;
  unit?: string;
  confirmLabel?: string;
  hint?: string;
  onConfirm: (value: number) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  function submit() {
    const n = Number(value.replace(",", "."));
    if (!Number.isFinite(n) || n <= 0) {
      setError("Bitte eine positive Zahl eingeben.");
      return;
    }
    onConfirm(n);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 460 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <div className="uppercase-mini">/ MENGEN / EINGABE</div>
            <div className="modal-title">{title}</div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          <label className="uppercase-mini" style={{ color: "var(--fg-dim)" }}>
            {label}
          </label>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
            <input
              ref={inputRef}
              type="text"
              inputMode="decimal"
              value={value}
              onChange={(e) => {
                setValue(e.currentTarget.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
              }}
              aria-label={label}
              className="input mono"
              style={{ flex: 1 }}
            />
            {unit && <span className="mono dim">{unit}</span>}
          </div>
          {hint && (
            <div className="uppercase-mini dim" style={{ marginTop: 6 }}>
              {hint}
            </div>
          )}
          {error && (
            <div
              className="warn-line"
              style={{ marginTop: 12, borderLeftColor: "var(--bad)" }}
            >
              <span className="mono">{error}</span>
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn ghost" onClick={onClose}>
            Abbrechen
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn primary" onClick={submit}>
            <Icon name="check" size={14} /> {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
